import React, { useState } from "react";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";

function StepContent({ step, title, description, image, isFirst, isLast, onNext, onBack, onFinish }) {
  return (
    <div className="transition-all">
      <h2 className="text-xl font-semibold mb-4 text-gray-700">{title}</h2>
      <p className="mb-4 text-gray-600 whitespace-pre-line">{description}</p>
      {image && (
        <img
          src={image}
          alt={`Step ${step} Screenshot`}
          className="mb-4 border-4 border-black rounded-md"
        />
      )}
      <div className="flex justify-between">
        {!isFirst && (
          <button
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 py-2 px-6 rounded-md font-medium"
            onClick={onBack}
          >
            Back
          </button>
        )}
        {!isLast ? (
          <button
            className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-6 rounded-md font-medium ml-auto"
            onClick={onNext}
          >
            Next
          </button>
        ) : (
          <button
            className="bg-green-600 hover:bg-green-700 text-white py-2 px-6 rounded-md font-medium ml-auto"
            onClick={onFinish}
          >
            Finish
          </button>
        )}
      </div>
    </div>
  );
}

function OnboardingWizard() {
  const [step, setStep] = useState(1);
  const totalSteps = 18;
  const navigate = useNavigate();

  const nextStep = () => setStep((prev) => prev + 1);
  const prevStep = () => setStep((prev) => prev - 1);
  const finishOnboarding = () => navigate("/generateDraft");

  const progressPercentage = Math.round((step / totalSteps) * 100);

  const steps = [
    {
      title: "Setting Up Basics",
      description: `Fill your name, company, and email in Settings. These fields will automatically appear in your email signatures and campaigns.`,
      image: "/assets/onboarding/step_1.png",
    },
    {
      title: "Create a Base Draft",
      description: `Click “New Draft” to start. Choose a goal, subject line, and short description to build your reusable base template.`,
      image: "/assets/onboarding/step_3.png",
    },
    {
      title: "Manage All Drafts",
      description: `Visit All Drafts to find, edit, or favorite drafts. Favorite drafts appear in dropdowns when personalizing or creating campaigns.`,
      image: "/assets/onboarding/step_5.png",
    },
    {
      title: "Select Draft for A/B Testing",
      description: `Pick a favorite draft to simulate personalization and test how personalized emails will look before sending real campaigns.`,
      image: "/assets/onboarding/step_6.png",
    },
    {
      title: "Personalize Draft - Input Data",
      description: `Enter the recipient's name (required) and optional fields like company, city, or website. The more you add, the better the personalization.`,
      image: "/assets/onboarding/step_7.png",
    },
    {
      title: "Personalize Draft - Improve",
      description: `Enhance personalization by adding extra details like purchase history or references to past interactions.`,
      image: "/assets/onboarding/step_8.png",
    },
    {
      title: "View Personalized Draft",
      description: `See how your draft transforms based on the data provided. Check for relevance and adjust if needed.`,
      image: "/assets/onboarding/step_9.png",
    },
    {
      title: "Optimize Draft (Anti-Spam)",
      description: `Run spam optimization to clean up trigger words. This maximizes your chances of inbox delivery.`,
      image: "/assets/onboarding/step_10.png",
    },
    {
      title: "Final Draft Ready",
      description: `Your draft is now ready! You can copy it to your clipboard or download it for use in campaigns.`,
      image: "/assets/onboarding/step_11.png",
    },
    {
      title: "Send Test Email",
      description: `Send a test email to yourself. Check how the final draft looks inside your inbox and confirm formatting.`,
      image: "/assets/onboarding/step_12.png",
    },
    {
      title: "Create a New Campaign",
      description: `Create a campaign by uploading a CSV list of recipients, selecting your draft, and scheduling a launch date.`,
      image: "/assets/onboarding/step_13.png",
    },
    {
      title: "View Pending Campaign",
      description: `Pending campaigns are waiting to launch. You can cancel them any time before the scheduled start.`,
      image: "/assets/onboarding/step_14.png",
    },
    {
      title: "View Completed Campaign",
      description: `Completed campaigns show a final status and allow you to view the performance results.`,
      image: "/assets/onboarding/step_15.png",
    },
    {
      title: "View Campaign Results",
      description: `See how your campaign performed, including delivery rates, open rates, bounces, and complaints.`,
      image: "/assets/onboarding/step_16.png",
    },
    {
      title: "LinkedIn Bulk Personalization",
      description: `Generate up to 20 personalized LinkedIn messages at once. Download the drafts and post them manually in LinkedIn.`,
      image: "/assets/onboarding/step_18.png",
    },
    {
      title: "Manage Credits",
      description: `Monitor your available credits here. Enable auto-renew when your balance falls below 10 credits.`,
      image: "/assets/onboarding/step_19.png",
    },
    {
      title: "Complete a Purchase",
      description: `Buy credits securely through Stripe. Access your invoices anytime for records or accounting.`,
      image: "/assets/onboarding/step_20.png",
    },
    {
      title: "Explore Draft Templates",
      description: `Browse hundreds of ready-made draft templates to help you create outreach emails faster and smarter.`,
      image: "/assets/onboarding/step_21.png",
    }
  ];

  const current = steps[step - 1];

  return (
    <Layout>
      <div className="container mx-auto mt-10 max-w-3xl">
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <p className="text-sm font-medium text-gray-700">Step {step} of {totalSteps}</p>
            <p className="text-sm font-medium text-gray-700">{progressPercentage}%</p>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
        </div>

        <div className="bg-white shadow-md p-8 rounded-md">
          <h1 className="text-2xl font-bold mb-6 text-gray-800">Onboarding Wizard</h1>

          <StepContent
            step={step}
            title={current.title}
            description={current.description}
            image={current.image}
            isFirst={step === 1}
            isLast={step === totalSteps}
            onNext={nextStep}
            onBack={prevStep}
            onFinish={finishOnboarding}
          />

          <div className="mt-8 flex justify-center">
            <button
              className="bg-red-500 hover:bg-red-600 text-white py-2 px-6 rounded-md font-medium"
              onClick={finishOnboarding}
            >
              Skip Onboarding
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default OnboardingWizard;
